import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { Info, CheckCircle2, HelpCircle } from 'lucide-react';
import axios from 'axios';
import ActivitesFiltre, { filtrerActivites, motsDisponibles } from './ActivitesFiltre';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SelecteurActivites = ({ categorie, selectedIds, onChange, label, onActivitesLoaded, coveredIds = [] }) => {
  const [activites, setActivites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDetailsOpen, setSelectedDetailsOpen] = useState(false);
  const [recherche, setRecherche] = useState('');
  const [motsActifs, setMotsActifs] = useState([]);
  const [seulementSelection, setSeulementSelection] = useState(false);
  const visibles = filtrerActivites(activites, recherche, motsActifs, selectedIds, seulementSelection);

  useEffect(() => {
    fetchActivites();
  }, [categorie]);

  const fetchActivites = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/activites-batico?categorie=${categorie}`);
      const loadedActivites = response.data;
      // Trier les activités par ordre alphabétique (par nom)
      const sortedActivites = [...loadedActivites].sort((a, b) => 
        (a.nom || '').localeCompare(b.nom || '', 'fr', { sensitivity: 'base' })
      );
      setActivites(sortedActivites);
      // Notifier le parent des activités chargées pour pouvoir récupérer les détails
      if (onActivitesLoaded && sortedActivites.length > 0) {
        onActivitesLoaded(categorie, sortedActivites);
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (activiteId) => {
    if (coveredIds.includes(activiteId)) return;
    const newSelection = selectedIds.includes(activiteId)
      ? selectedIds.filter(id => id !== activiteId)
      : [...selectedIds, activiteId];
    onChange(newSelection);
  };

  const getCategoryLabel = (cat) => {
    const labels = {
      gros_oeuvre: 'Gros Œuvre',
      second_oeuvre: 'Second Œuvre',
      travaux_publics: 'Travaux Publics',
      intellectuels: 'Professionnels Intellectuels'
    };
    return labels[cat] || cat;
  };

  const getSelectedActivites = () => {
    return activites.filter(act => selectedIds.includes(act.id));
  };

  if (loading) {
    return <div className="text-sm text-gray-500">Chargement des activités...</div>;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium">
          {label || getCategoryLabel(categorie)}
        </label>
        <Badge className={selectedIds.length > 0 ? "bg-blue-700 text-white" : "bg-slate-700 text-white"}>
          {selectedIds.length} sélectionné{selectedIds.length > 1 ? 's' : ''}
        </Badge>
      </div>

      {selectedIds.length > 0 && (
        <Dialog open={selectedDetailsOpen} onOpenChange={setSelectedDetailsOpen}>
          <DialogTrigger asChild>
            <button className="text-xs text-blue-600 hover:underline flex items-center gap-1">
              <Info className="h-3 w-3" />
              Voir les activités sélectionnées
            </button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[70vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{getCategoryLabel(categorie)} - Activités sélectionnées</DialogTitle>
              <DialogDescription>
                {selectedIds.length} activité{selectedIds.length > 1 ? 's' : ''} sélectionnée{selectedIds.length > 1 ? 's' : ''}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              {getSelectedActivites().map(act => (
                <Card key={act.id}>
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-sm">{act.code} - {act.nom}</CardTitle>
                        {act.referentiel && (
                          <CardDescription className="text-xs mt-1">{act.referentiel}</CardDescription>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  {act.exclusions && act.exclusions.length > 0 && (
                    <CardContent className="pt-0">
                      <div className="text-xs">
                        <span className="font-semibold text-red-600">Exclusions :</span>
                        <ul className="list-disc list-inside ml-2 mt-1 text-gray-600">
                          {act.exclusions.map((excl, idx) => (
                            <li key={idx}>{excl}</li>
                          ))}
                        </ul>
                      </div>
                    </CardContent>
                  )}
                </Card>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      )}

      <ActivitesFiltre categorie={categorie} mots={motsDisponibles(activites)} recherche={recherche} setRecherche={setRecherche}
        motsActifs={motsActifs} setMotsActifs={setMotsActifs} seulementSelection={seulementSelection}
        setSeulementSelection={setSeulementSelection} total={activites.length} visibles={visibles.length} />

      <div className="border border-slate-200/80 rounded-xl max-h-[26rem] overflow-y-auto bg-white shadow-sm">
        <TooltipProvider delayDuration={200}>
          <div className="divide-y divide-slate-100">
            {visibles.length === 0 && (
              <p className="px-4 py-6 text-center text-sm text-slate-500" data-testid={`filtre-aucun-${categorie}`}>Aucune activité ne correspond à ces filtres.</p>
            )}
            {visibles.map((activite) => {
              const isSelected = selectedIds.includes(activite.id);
              const isCovered = coveredIds.includes(activite.id);
              const hasExclusions = activite.exclusions && activite.exclusions.length > 0;

              return (
                <Tooltip key={activite.id}>
                  <TooltipTrigger asChild>
                    <div
                      onClick={() => handleToggle(activite.id)}
                      aria-disabled={isCovered}
                      data-testid={isCovered ? `activite-couverte-${activite.code}` : undefined}
                      className={`group relative px-4 py-3 transition-all duration-200 ${
                        isCovered ? 'cursor-not-allowed bg-slate-100/80 opacity-60'
                        : isSelected
                          ? 'cursor-pointer bg-gradient-to-r from-indigo-50 via-blue-50/60 to-transparent'
                          : 'cursor-pointer hover:bg-slate-50/80'
                      }`}
                    >
                      {/* Liseré gauche sur sélection (institutionnel) */}
                      <span
                        className={`absolute left-0 top-0 bottom-0 w-1 rounded-r ${
                          isSelected
                            ? 'bg-gradient-to-b from-indigo-600 to-blue-700'
                            : 'bg-transparent group-hover:bg-slate-200'
                        }`}
                        aria-hidden
                      />
                      <div className="flex items-start gap-3 pl-1">
                        {/* Checkbox accessible */}
                        <div className="flex-shrink-0 mt-0.5">
                          <div
                            className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${
                              isSelected
                                ? 'bg-gradient-to-br from-indigo-600 to-blue-700 border-blue-700 shadow-sm'
                                : 'border-slate-300 group-hover:border-indigo-300 bg-white'
                            }`}
                          >
                            {isSelected && <CheckCircle2 className="h-3.5 w-3.5 text-white" strokeWidth={3} />}
                          </div>
                        </div>

                        {/* Contenu principal */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span
                              className={`inline-flex items-center font-mono text-[10px] font-bold tracking-wider px-2 py-0.5 rounded uppercase ${
                                isSelected
                                  ? 'bg-indigo-700 text-white'
                                  : 'bg-slate-100 text-slate-700 group-hover:bg-indigo-100 group-hover:text-indigo-800'
                              }`}
                            >
                              {activite.code}
                            </span>
                            {isCovered && (
                              <span className="inline-flex items-center rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-800">Déjà couverte</span>
                            )}
                            <span
                              className={`text-sm font-semibold leading-snug ${
                                isSelected ? 'text-indigo-900' : 'text-slate-900'
                              }`}
                            >
                              {activite.nom}
                            </span>
                            <HelpCircle
                              className={`h-3.5 w-3.5 flex-shrink-0 ${
                                isSelected ? 'text-indigo-400' : 'text-slate-300 group-hover:text-indigo-400'
                              }`}
                            />
                          </div>
                          {activite.referentiel && (
                            <p className="text-[11.5px] text-slate-500 mt-1 line-clamp-2 leading-relaxed">
                              {activite.referentiel}
                            </p>
                          )}
                          {hasExclusions && (
                            <div className="mt-1.5 inline-flex items-center gap-1">
                              <span className="w-1.5 h-1.5 rounded-full bg-amber-500" aria-hidden />
                              <span className="text-[10.5px] font-semibold text-amber-700 uppercase tracking-wide">
                                {activite.exclusions.length} exclusion{activite.exclusions.length > 1 ? 's' : ''} — survoler pour voir
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent 
                    side="right" 
                    align="start" 
                    className="max-w-md p-4 bg-white border-2 border-gray-200 shadow-lg z-50"
                  >
                    <div className="space-y-3">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className="bg-blue-700 text-white font-semibold">{activite.code}</Badge>
                          <span className="font-semibold text-sm">{activite.nom}</span>
                        </div>
                        {activite.referentiel && (
                          <div className="text-sm text-gray-700 mb-2">
                            <span className="font-semibold text-gray-900">Définition :</span>
                            <p className="mt-1">{activite.referentiel}</p>
                          </div>
                        )}
                      </div>
                      
                      {hasExclusions && (
                        <div className="border-t pt-2">
                          <div className="flex items-center gap-1 mb-2">
                            <span className="text-xs font-bold text-red-600">⚠️ EXCLUSIONS :</span>
                          </div>
                          <ul className="list-disc list-inside space-y-1 text-xs text-gray-700">
                            {activite.exclusions.map((excl, idx) => (
                              <li key={idx} className="text-red-700">{excl}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {!hasExclusions && (
                        <div className="text-xs text-green-600 italic">
                          ✓ Aucune exclusion
                        </div>
                      )}
                    </div>
                  </TooltipContent>
                </Tooltip>
              );
            })}
          </div>
        </TooltipProvider>
      </div>

      {categorie === 'intellectuels' && (
        <div className="text-xs bg-blue-50 border border-blue-200 rounded p-2 text-blue-700">
          <Info className="h-3 w-3 inline mr-1" />
          Les activités PIB sont calculées indépendamment des autres activités
        </div>
      )}
    </div>
  );
};

export default SelecteurActivites;
